/**
 * +presence: the other players of a shared field as the client knows them (pure, no DOM / clock).
 *
 * Snapshots carry the shard's time `ts`. The client learns `offset` = local − server time as a
 * min-filter over arrivals (jitter only delays, so the minimum is the link itself; it creeps up
 * 0.5 ms per snapshot so a slower link is followed), and draws others INTERP_MS in the past:
 * between two samples it interpolates; past the newest it extrapolates along the reported
 * velocity for at most EXTRAP_MAX_MS, then holds. Events (casts, jumps, chat…) are released on
 * the same timeline, so a cast starts where the caster is drawn. A waystone jump ('travel') marks
 * its arrival sample as a cut: the avatar holds, then snaps (no slide across the map).
 */
import type { FieldEventOut, FieldPlayerInfo, FieldServerMsg, FieldState } from './fieldProtocol';

export const INTERP_MS = 120;
export const EXTRAP_MAX_MS = 200;
const MAX_SAMPLES = 24;
const CREEP_MS = 0.5;

export interface Sample extends FieldState {
  ts: number;
  /** +presence: a waystone arrival: drawn where the previous sample was until `ts`, then here (no slide). */
  cut?: boolean;
}

export interface RemotePlayer {
  id: number;
  name: string;
  cls: string;
  lvl: number;
  look: number;
  afk: boolean;
  samples: Sample[];
}

export interface Pose {
  x: number;
  y: number;
  f: number;
  vx: number;
  vy: number;
  an: number;
  c: string | undefined;
  tk: 'e' | 'f' | undefined;
  /** +glide: absolute height in the air / on a platform (undefined on the ground). */
  z?: number;
}

export interface TimedEvent {
  ts: number;
  e: FieldEventOut;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  else if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

export class PresenceModel {
  readonly players = new Map<number, RemotePlayer>();
  you = -1;
  shard = -1;
  /** local − server clock (ms); NaN until the first snapshot. */
  offset = NaN;
  private events: TimedEvent[] = [];
  /** Server time of the newest message (welcome states are placed here). */
  private lastTs = NaN;

  reset(): void {
    this.players.clear();
    this.events = [];
    this.you = -1;
    this.shard = -1;
    this.offset = NaN;
    this.lastTs = NaN;
  }

  /** Apply one server message received at local time `now`. Returns players that joined / left. */
  apply(msg: FieldServerMsg, now: number): { joined?: RemotePlayer; left?: RemotePlayer } {
    switch (msg.t) {
      case 'welcome': {
        this.reset();
        this.you = msg.you;
        this.shard = msg.shard;
        for (const p of msg.players) this.add(p, now);
        return {};
      }
      case 'join': {
        if (msg.p.id === this.you) return {};
        return { joined: this.add(msg.p, now) };
      }
      case 'leave': {
        const p = this.players.get(msg.id);
        this.players.delete(msg.id);
        this.events = this.events.filter((x) => x.e.id !== msg.id);
        return p ? { left: p } : {};
      }
      case 'snap': {
        this.clock(msg.ts, now);
        for (const r of msg.s) {
          const p = this.players.get(r[0]);
          if (!p) continue;
          const s: Sample = { ts: msg.ts, x: r[1], y: r[2], f: r[3], vx: r[4], vy: r[5], an: r[6] };
          if (r[7]) s.c = r[7];
          if (r[8] === 'e' || r[8] === 'f') s.tk = r[8];
          if (typeof r[9] === 'number' && Number.isFinite(r[9])) s.z = r[9]; // +glide
          // A sample older than the newest one (never on an ordered socket) is ignored.
          const last = p.samples[p.samples.length - 1];
          if (last && last.ts > s.ts) continue;
          if (last && last.ts === s.ts) p.samples[p.samples.length - 1] = s;
          else p.samples.push(s);
          if (p.samples.length > MAX_SAMPLES) p.samples.splice(0, p.samples.length - MAX_SAMPLES);
        }
        // Out of interest range: forget where they were (no frozen ghost); a full row comes on re-entry.
        for (const id of msg.o ?? []) {
          const p = this.players.get(id);
          if (p) p.samples.length = 0;
        }
        for (const e of msg.ev ?? []) {
          if (e.id === this.you || !this.players.has(e.id)) continue;
          const p = this.players.get(e.id)!;
          if (e.k === 'lvl') p.lvl = e.lvl;
          else if (e.k === 'afk') p.afk = e.on;
          else if (e.k === 'travel' && !msg.o?.includes(e.id)) this.cut(p, msg.ts, e.to);
          this.events.push({ ts: msg.ts, e });
        }
        return {};
      }
      default:
        return {};
    }
  }

  /** +presence: a waystone jump lands at `to` at server time `ts`: the sample there becomes a cut. */
  private cut(p: RemotePlayer, ts: number, to: [number, number]): void {
    let s = p.samples.find((x) => x.ts === ts);
    if (!s) {
      const last = p.samples[p.samples.length - 1];
      if (last && last.ts > ts) return;
      s = { ...(last ?? { f: 0, an: 0 }), ts, x: to[0], y: to[1], vx: 0, vy: 0 };
      p.samples.push(s);
    }
    s.x = to[0];
    s.y = to[1];
    s.cut = true;
  }

  private add(info: FieldPlayerInfo, now: number): RemotePlayer {
    const p: RemotePlayer = { id: info.id, name: info.name, cls: info.cls, lvl: info.lvl, look: info.look, afk: info.afk, samples: [] };
    if (info.s) {
      // Known state without a snapshot time yet: pin it at "now" on the server clock (or earlier).
      const ts = Number.isFinite(this.offset) ? now - this.offset : Number.isFinite(this.lastTs) ? this.lastTs : 0;
      p.samples.push({ ...info.s, ts });
    }
    this.players.set(p.id, p);
    return p;
  }

  private clock(ts: number, now: number): void {
    this.lastTs = ts;
    const cand = now - ts;
    if (!Number.isFinite(this.offset) || cand < this.offset) this.offset = cand;
    else this.offset += Math.min(cand - this.offset, CREEP_MS);
    // Welcome states pinned before the clock was known sit at ts 0: move them to this snapshot.
    for (const p of this.players.values()) for (const s of p.samples) if (s.ts === 0) s.ts = ts - INTERP_MS;
  }

  /** The server time others are drawn at (INTERP_MS in the past). */
  renderTime(now: number): number {
    return Number.isFinite(this.offset) ? now - this.offset - INTERP_MS : -Infinity;
  }

  /** Where `p` is drawn at server time `t` (null before its first state). */
  pose(p: RemotePlayer, t: number): Pose | null {
    const ss = p.samples;
    if (!ss.length) return null;
    const first = ss[0]!;
    if (t <= first.ts) return poseOf(first);
    for (let i = ss.length - 1; i > 0; i--) {
      const a = ss[i - 1]!;
      const b = ss[i]!;
      if (t >= a.ts && t <= b.ts) {
        // +presence: into a waystone arrival there is no in-between: hold, then snap.
        const k = b.cut ? (t >= b.ts ? 1 : 0) : b.ts > a.ts ? (t - a.ts) / (b.ts - a.ts) : 1;
        const out: Pose = { x: lerp(a.x, b.x, k), y: lerp(a.y, b.y, k), f: lerpAngle(a.f, b.f, k), vx: lerp(a.vx, b.vx, k), vy: lerp(a.vy, b.vy, k), an: k < 0.5 ? a.an : b.an, c: k < 0.5 ? a.c : b.c, tk: k < 0.5 ? a.tk : b.tk };
        // +glide: the height eases between two airborne samples; taking off / landing holds the nearer one.
        if (a.z !== undefined && b.z !== undefined) out.z = lerp(a.z, b.z, k);
        else if ((k < 0.5 ? a : b).z !== undefined) out.z = (k < 0.5 ? a : b).z;
        return out;
      }
    }
    // Past the newest sample: extrapolate along its velocity, clamped.
    const last = ss[ss.length - 1]!;
    const dt = Math.min(t - last.ts, EXTRAP_MAX_MS) / 1000;
    const out = poseOf(last);
    out.x += last.vx * dt;
    out.y += last.vy * dt;
    return out;
  }

  /** Events due at server time `t` (in order), removed from the queue. */
  due(t: number): FieldEventOut[] {
    if (!this.events.length || this.events[0]!.ts > t) return [];
    let n = 0;
    while (n < this.events.length && this.events[n]!.ts <= t) n++;
    return this.events.splice(0, n).map((x) => x.e);
  }

  /** Drop samples older than needed (keep one before `t` for interpolation). */
  trim(t: number): void {
    for (const p of this.players.values()) {
      const ss = p.samples;
      let i = 0;
      while (i + 1 < ss.length && ss[i + 1]!.ts <= t) i++;
      if (i > 0) ss.splice(0, i);
    }
  }
}

function poseOf(s: Sample): Pose {
  return { x: s.x, y: s.y, f: s.f, vx: s.vx, vy: s.vy, an: s.an, c: s.c, tk: s.tk, ...(s.z !== undefined ? { z: s.z } : {}) };
}
